import { api, unwrap, getToken, type APIResponse } from "./client";

// Mirrors app/routers/agent.py's LeadContext: what the NUMBER says when the
// lead record is thin. Every field nullable — null means "not known", never a
// guessed default, so the console leaves the line out rather than print a
// wrong state or local time.
export interface LeadContext {
  area_code: string | null;
  state: string | null;
  timezone: string | null;
  local_time: string | null;
  list_name: string | null;
  attempt: number | null;
  try_allowed: number | null;
  carrier: string | null;
  line_type: string | null;
}

export interface PriorCall {
  at: string;
  campaign_id: number | null;
  campaign_name: string | null;
  outcome: string;
  billsec: number;
  agent_name: string | null;
}

export interface DncFlag {
  listed_at: string | null;
  reason: string | null;
}

// Where a name on the pop came from when the list had none: a rep on this
// account wrote it down on an earlier call (F6). Optional so older fixtures
// and callers need not spell it out.
export interface KnownName {
  first_name: string | null;
  last_name: string | null;
  at: string | null;
  agent_name: string | null;
}

export interface CurrentCall {
  // null for a live call with no campaign behind it (inbound, or a customer
  // leg carrying no spool id). Still a real caller with a number and a
  // history; there is just nothing to disposition.
  spool_id: number | null;
  campaign_id: number | null;
  campaign_name: string | null;
  phone: string;
  first_name: string | null;
  last_name: string | null;
  status: string;
  // Per-lead custom fields captured at upload (contact.extra_data). Empty {}
  // when the lead has none. Rendered as a screen-pop in the console.
  custom_fields: Record<string, string>;
  // false while the seat is still being rung — the pop now fires from the
  // alerting leg so the agent sees who it is BEFORE picking up. Never offer
  // wrap-up for a pop that was never answered.
  answered: boolean;
  email: string | null;
  lead: LeadContext;
  prior_calls: number;
  history: PriorCall[];
  dnc: DncFlag | null;
  known_name?: KnownName | null;
}

export async function getCurrentCall(extensionId: number): Promise<CurrentCall | null> {
  const resp = await api.get<APIResponse<CurrentCall | null>>("/agent/current-call", {
    params: { extension_id: extensionId },
  });
  // `data: null` is the normal idle state (no live call) — must not throw, or the
  // 2s poll sits permanently in an error/retry state. unwrap() rejects null, so
  // handle this nullable endpoint explicitly.
  if (!resp.data.success) {
    throw new Error(resp.data.error || "Request failed");
  }
  return resp.data.data;
}

// Mirrors app/routers/agent.py's AgentMe (GET /api/agent/me) — the agent
// panel's source of truth for its own seat. Agent principals only; an
// operator token has no agent_user_id and 403s this endpoint.
export interface AgentMe {
  agent_user_id: number;
  username: string;
  display_name: string | null;
  extension_id: number;
  extension_name: string;
}

export async function getAgentMe(): Promise<AgentMe> {
  const resp = await api.get<APIResponse<AgentMe>>("/agent/me");
  return unwrap(resp.data);
}

// Mirrors app/routers/agent.py's DialInfo (GET /api/agent/dial-info) — whether
// this seat may dial out by hand, and which number it will show. Advisory: the
// dialplan re-checks all of it against the authenticated endpoint, so this only
// decides what the console offers, never what the PBX allows.
export interface DialInfo {
  can_dial: boolean;
  callerid: string | null;
  reason: string | null;
}

export async function getDialInfo(extensionId: number): Promise<DialInfo> {
  const resp = await api.get<APIResponse<DialInfo>>("/agent/dial-info", {
    params: { extension_id: extensionId },
  });
  return unwrap(resp.data);
}

export interface AgentDisposition {
  disposition_id: number;
  name: string;
  description: string | null;
}

/** The wrap-up list, fetched from an endpoint an AGENT can actually reach.
 *
 * GET /lookup/dispositions is gated by require_permission('disposition'), whose
 * chain runs get_current_user, which 403s every AgentPrincipal — so an agent
 * login rendered a 'Disposition' header with zero buttons and could never wrap
 * up a call (beads pz2z). It also meant the Do Not Call disposition added for
 * agents was invisible to the people it was added for. */
export async function listAgentDispositions(): Promise<AgentDisposition[]> {
  const resp = await api.get<APIResponse<AgentDisposition[]>>("/agent/dispositions");
  return unwrap(resp.data);
}

export async function submitDisposition(spoolId: number, disposition: string): Promise<void> {
  const resp = await api.post<APIResponse<{ recorded: boolean }>>("/agent/disposition", {
    spool_id: spoolId,
    disposition,
  });
  unwrap(resp.data);
}

// Mirrors app/routers/agent.py's SipCredentials response model
// (GET /api/agent/sip-credentials) — everything SIP.js needs to register a
// browser softphone for the caller's own extension.
//
// SipIceServer mirrors app/routers/agent.py's IceServer pydantic model
// (formerly an untyped `list[dict]` on the backend — see that model's
// docstring for the contract drift this closes). `username`/`credential`
// are optional here because the current STUN-only server sends neither;
// they exist so a future TURN entry (the plan's own NAT-traversal risk row
// anticipates one) has a typed place to land on this side instead of
// silently violating this interface at runtime.
export interface SipIceServer {
  urls: string;
  username?: string;
  credential?: string;
}

export interface SipCredentials {
  uri: string;
  ws_server: string;
  auth_user: string;
  password: string;
  display_name: string;
  ice_servers: SipIceServer[];
}

export async function getSipCredentials(extensionId: number): Promise<SipCredentials> {
  const resp = await api.get<APIResponse<SipCredentials>>("/agent/sip-credentials", {
    params: { extension_id: extensionId },
  });
  return unwrap(resp.data);
}

// ---------------------------------------------------------------------------
// Presence — mirrors app/routers/agent.py's login/logout/pause/heartbeat.
// Every call is scoped to the caller's own extension_id server-side (404 for
// anything else), so no ownership handling is needed on this side.
// ---------------------------------------------------------------------------

export async function agentLogin(extensionId: number): Promise<void> {
  const resp = await api.post<APIResponse<unknown>>("/agent/login", { extension_id: extensionId });
  unwrap(resp.data);
}

export async function agentLogout(extensionId: number): Promise<void> {
  const resp = await api.post<APIResponse<unknown>>("/agent/logout", { extension_id: extensionId });
  unwrap(resp.data);
}

export async function agentPause(
  extensionId: number,
  paused: boolean,
  reason?: string,
): Promise<void> {
  const resp = await api.post<APIResponse<unknown>>("/agent/pause", {
    extension_id: extensionId,
    paused,
    reason,
  });
  unwrap(resp.data);
}

export interface HeartbeatResult {
  extension_id: number;
  /** The reaper had wrongly logged this seat out and the beat put it back. */
  restored: boolean;
  /** The SERVER no longer considers this seat signed in — the idle sweep, or
   *  a manager pressing Sign out on the floor board. The console must stop
   *  claiming Ready and say so. */
  signed_out: boolean;
  /** 'unanswered' (the sweep) or 'manager', when known. */
  signed_out_reason: string | null;
  paused: boolean;
  /** The console holds a SIP registration but Asterisk cannot confirm it can
   *  ring this seat. The console would otherwise keep showing "Ready" while
   *  the manager board correctly shows them as unreachable — and the rep is
   *  the only person who does not know. */
  unreachable: boolean;
}

export async function agentHeartbeat(extensionId: number): Promise<HeartbeatResult> {
  const resp = await api.post<APIResponse<HeartbeatResult>>("/agent/heartbeat", {
    extension_id: extensionId,
  });
  return unwrap(resp.data);
}

/**
 * Best-effort logout fired from `window.onbeforeunload`.
 *
 * `navigator.sendBeacon` can't carry the `Authorization` header this endpoint
 * requires (it only ever sends a body), so this uses `fetch(..., {keepalive:
 * true})` instead — the browser keeps a keepalive request alive past page
 * unload the same way it does for sendBeacon, but we can still set headers.
 * Fire-and-forget: the 90s Redis-TTL presence reaper is the backstop if this
 * never lands, so failures here are intentionally swallowed.
 */
export function logoutBeacon(extensionId: number): void {
  const token = getToken();
  try {
    void fetch("/api/agent/logout", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ extension_id: extensionId }),
      keepalive: true,
    });
  } catch {
    // best-effort — see doc comment above
  }
}
